import react, { useMemo, useCallback } from 'react';
import styled from '../styled';
import View from './View';
import ReactDOM from 'react-dom';
import fromTheme from '../fromTheme';
import useModalQueue from '../hooks/useModalQueue';
import Toast from './Toast';
import PortalHost from './contextProviders/PortalHost';
import Tooltip from './Tooltip';
import { ModalContext } from '../contexts';
import webOnlyStyles from '../webOnlyStyles';
import nativeOnlyStyles from '../nativeOnlyStyles';
import { useTheme } from 'styled-components';
const { createElement: rc, useEffect, forwardRef } = react;

const ModalContainer = styled(View).attrs({
    name: 'modal-container'
})`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    width: 100%;
    overflow: hidden;
    position: absolute;
    background-color: ${fromTheme('modal', 'backgroundFade')};
`;
ModalContainer.displayName = 'ModalContainer';

// height 100% needed for scrolling in modal content
let ModalContent = styled(View).attrs({
    name: 'modal-content'
})`
    position: ${({ theme: { mobile } }) => (mobile ? 'absolute' : 'unset')};
    top: 0;
    left: 0;
    background-color: ${fromTheme('modal', 'backgroundColor')};
    color: white;
    flex-direction: column;
    align-items: stretch;
    ${fromTheme('modal', 'font')};
    padding: ${fromTheme('modal', 'padding')};
    border-radius: ${fromTheme('modal', 'borderRadius')};
    min-height: ${fromTheme('modal', 'minHeight')};
    min-width: ${fromTheme('modal', 'minWidth')};
    max-width: ${fromTheme('modal', 'maxWidth')};
    overflow: hidden;
    overflow-y: ${({ scrollingRequired }) => (scrollingRequired ? 'auto' : 'unset')};
    height: ${({ scrollingRequired }) => (scrollingRequired ? '100%' : 'unset')};
    flex-grow: 1;
`;
ModalContent = webOnlyStyles(ModalContent)`
    box-shadow: ${fromTheme('modal', 'boxShadow')};
`;
ModalContent = nativeOnlyStyles(ModalContent)`
    elevation: ${fromTheme('modal', 'native', 'elevation')};
`;
ModalContent.displayName = 'ModalContent';

const Modal = forwardRef((props, ref) => {
    const dialog = useMemo(() => document.querySelector('dialog'), []);
    const { id, jumpQueue, visible, scrollingRequired = false, onChange } = props || {};
    const theme = useTheme();
    const showModal = useModalQueue(visible, id, jumpQueue);

    const preventEscOrEnterClose = useCallback(e => {
        e.preventDefault();
    }, []);

    useEffect(() => {
        // Prevent the dialog from closing when the user presses the Enter or Escape key.
        dialog.addEventListener('keydown', e => {
            if (e.key === 'Enter' || e.key === 'Escape') {
                preventEscOrEnterClose(e);
            }
        });
        return () => {
            dialog.removeEventListener('keydown', preventEscOrEnterClose);
        };
    }, [dialog, preventEscOrEnterClose]);

    // When unmounting, call onChange.  Use the change type 'unmount'
    // (as opposed to 'close') to avoid causing side-effects in existing
    // code.
    useEffect(() => {
        return () => onChange?.('unmount');
    }, [onChange]);

    const { children, ...otherProps } = props;

    if (!showModal) return null;

    const forwardToast = theme.mobile;

    // prettier-ignore
    return rc(ModalContext.Provider, { value: { isInsideModal: true } },
        ReactDOM.createPortal(
            rc(ModalContainer, { ref },
                rc(PortalHost, null,
                    rc(ModalContent, { ...otherProps, scrollingRequired }, children),
                    forwardToast && rc(Toast),
                    rc(Tooltip.Popup)
                )
            ), dialog
        )
    );
});
Modal.displayName = 'Modal';
export default Modal;
